import type { CallEvent, CallRequest } from './validate-api';
import type { ValidateTurn } from './validate-runs';

/**
 * A "Call me" call as chat bubbles, from what the logs hold
 * (`bobcat/src/server-only/engine-logs/call-transcript.ts`).
 *
 * The caller's words are the engine's "last user input". The bot's words are
 * the message of the node each walk stopped at, looked up in the solution —
 * and replaced by the real text when a later request logs it as the "last
 * user-facing message" (how a generated answer's words come back). A request
 * that stops on a node with nothing to say (a hidden platform round-trip, the
 * exit) adds no bubble.
 *
 * Each caller turn is stamped with the start of the first request after the
 * previous reply, so the engine trace lines up under the reply it belongs to
 * (`matchTraceToReplies`), the same as a typed test.
 */

const HAS_VARIABLE = /\{[A-Z0-9_]+\}/;

const EVENT_TEXT: Record<CallEvent['kind'], string> = {
  'transfer-started': 'Transferring the call to an agent…',
  'agent-answered': 'An agent answered. The bot is off the call.',
  'no-destination':
    'No transfer destination is set on this voice channel, so the call ended.',
  'conference-ended': 'The call ended.',
};

export const buildCallTranscript = (
  requests: CallRequest[],
  events: CallEvent[],
  nodeMessages: ReadonlyMap<number, string>
): ValidateTurn[] => {
  const turns: (ValidateTurn & { _at: number })[] = [];
  let lastBot: (ValidateTurn & { _at: number }) | null = null;
  let turnStart: number | null = null;

  requests.forEach((req) => {
    turnStart ??= req.at;

    // The real words of the reply before this request, generated or not.
    if (req.lastSaid && lastBot) {
      lastBot.text = req.lastSaid;
    }

    if (req.userInput) {
      turns.push({
        role: 'user',
        text: req.userInput,
        at: turnStart,
        _at: turnStart,
      });
    }

    const message =
      req.stopNode !== undefined && req.stopNode >= 0
        ? nodeMessages.get(req.stopNode)?.trim()
        : undefined;
    // A web chat's start can walk to the greeting twice with nothing said in
    // between; one greeting is what the customer saw.
    const repeat =
      !!message &&
      turns[turns.length - 1] === lastBot &&
      lastBot?.text === message;
    if (repeat) turnStart = null;
    if (message && !repeat) {
      const bot = {
        role: 'bot' as const,
        text: HAS_VARIABLE.test(message)
          ? 'Generated answer — its words appear once the next turn is logged.'
          : message,
        _at: req.at,
      };
      turns.push(bot);
      lastBot = bot;
      turnStart = null;
    }
  });

  events.forEach((e) => {
    turns.push({
      role: 'bot',
      system: true,
      text: EVENT_TEXT[e.kind],
      _at: e.at,
    });
  });

  return turns
    .map((t, i) => ({ t, i }))
    .sort((a, b) => a.t._at - b.t._at || a.i - b.i)
    .map(({ t }) => {
      const { _at: _ignored, ...turn } = t;
      return turn;
    });
};

/** The integrator id in a voice webhook: `…/integrator/<id>/twilio_voice/start`. */
export const integratorIdOf = (webhookUrl: string | undefined): string | null =>
  /\/integrator\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\//i.exec(
    webhookUrl ?? ''
  )?.[1] ?? null;
